import { CacheKey } from "@/constants/definitions";
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export type CacheRecord<T> = {
  version: number; // app cache schema version
  ttlMs: number; // time-to-live in ms
  createdAt: number; // Date.now()
  etag?: string; // last ETag seen
  lastModified?: string; // last Last-Modified seen
  value: T;
};

const CACHE_VERSION = 1; // bump when schema or shape changes
const DB_NAME = "nimis-db";
const DB_VERSION = 1;
const STORE = "nimis-store";

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheDB extends DBSchema {
  [STORE]: {
    key: string;
    value: CacheRecord<unknown>;
  };
}

let dbPromise: Promise<IDBPDatabase<CacheDB>> | null = null;
async function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<CacheDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      },
    });
  }
  return dbPromise;
}

function makeKey(key: CacheKey): string {
  return `cached:${key}`;
}

export async function setCache<T>(
  key: CacheKey,
  value: T,
  opts?: { ttlMs?: number; etag?: string; lastModified?: string }
) {
  const record: CacheRecord<T> = {
    version: CACHE_VERSION,
    ttlMs: opts?.ttlMs ?? DEFAULT_TTL_MS,
    createdAt: Date.now(),
    etag: opts?.etag,
    lastModified: opts?.lastModified,
    value,
  };
  const db = await getDB();
  await db.put(STORE, record as CacheRecord<unknown>, makeKey(key));
}

export async function getCache<T>(
  key: CacheKey
): Promise<CacheRecord<T> | null> {
  const db = await getDB();
  const rec = (await db.get(STORE, makeKey(key))) as CacheRecord<T> | undefined;
  if (!rec) return null;
  if (rec.version !== CACHE_VERSION) {
    await db.delete(STORE, makeKey(key));
    return null;
  }
  const isExpired = Date.now() - rec.createdAt > rec.ttlMs;

  // Return even if expired so caller can do stale-while-revalidate
  // Consumer can check ttlMs === 0 to know the data is stale
  return { ...rec, ttlMs: isExpired ? 0 : rec.ttlMs };
}

// Cache Then Refresh
// -------------------
// The app's standard read strategy for anything preloaded at login: apply the
// cached value immediately (even if past its TTL) so the surface paints without
// waiting on the network, then fetch fresh, re-apply, and write the cache back.
// `apply` is therefore called up to twice — once per successful source — so
// call sites can drive a single setState from each.
//
// Lives here rather than in App.tsx (where it started) so surfaces outside the
// login bootstrap — the entitlements hook, for one — read through exactly the
// same strategy instead of reinventing it with subtly different semantics.
//
// Fetch and cache failures are non-fatal by default: a cache miss just means a
// slower first paint, and a failed fetch leaves whatever the cache had. Pass
// onError when the caller needs to surface the failure (e.g. an empty list that
// would otherwise read as "you own nothing" rather than "we couldn't check").
export async function cacheThenRefresh<T>(
  cacheKey: CacheKey,
  fetcher: () => Promise<T>,
  apply: (data: T) => void,
  opts?: { onError?: (error: unknown) => void }
): Promise<void> {
  // 1. Try cache first — regardless of TTL, show something fast
  try {
    const cached = await getCache<T>(cacheKey);
    if (cached?.value) {
      apply(cached.value);
    }
  } catch {
    // Cache read failures are non-fatal
  }

  // 2. Fetch fresh and refresh the cache
  try {
    const fresh = await fetcher();
    if (fresh !== null && fresh !== undefined) {
      apply(fresh);
      setCache(cacheKey, fresh);
    }
  } catch (refreshError) {
    // Network errors are non-fatal — we'll either have cached data or empty state
    opts?.onError?.(refreshError);
  }
}

export async function cacheHas(key: CacheKey): Promise<boolean> {
  const db = await getDB();
  const rec = await db.get(STORE, makeKey(key));
  return rec != null;
}

export async function purgeCache() {
  const db = await getDB();
  const tx = db.transaction(STORE, "readwrite");
  await tx.store.clear();
  await tx.done;
}

export async function keys(): Promise<string[]> {
  const db = await getDB();
  const ks: string[] = [];
  let cursor = await db.transaction(STORE).store.openKeyCursor();
  while (cursor) {
    ks.push(String(cursor.key));
    cursor = await cursor.continue();
  }
  return ks;
}
